import React, { useState, useRef, useEffect } from "react";
import {
    DownloadOutlined,
    AppleOutlined,
    AndroidOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import { Menu, Button, Card, Row, Col, Collapse } from "antd";
import { useTranslation } from "react-i18next";
import Footer from "../../components/Footer/Footer";
import "./HomePage.css";

const { Panel } = Collapse;

/**
 * Home Page Component
 *
 * The main landing page for the GlitGo website. Features:
 * - Responsive navigation
 * - Hero section with call-to-action
 * - App download section
 * - About section with service information
 * - FAQ section
 * - Contact information
 * - Smooth scroll navigation
 * - Multi-language support
 *
 * @component
 */
export const HomePage = () => {
    const { t } = useTranslation();

    // Refs for scroll sections
    const homeRef = useRef(null);
    const downloadRef = useRef(null);
    const aboutRef = useRef(null);
    const faqRef = useRef(null);
    const contactRef = useRef(null);

    // Navigation menu items
    const items = [
        { label: t("home.nav.home"), key: "home" },
        {
            label: t("home.nav.download"),
            key: "download",
            icon: <DownloadOutlined />,
        },
        { label: t("home.nav.about"), key: "about" },
        { label: t("home.nav.faq"), key: "faq" },
        { label: t("home.nav.contact"), key: "contact" },
    ];

    const [current, setCurrent] = useState("home");

    /**
     * Handle navigation menu clicks
     *
     * @param {Object} e - Click event
     * @param {string} e.key - Menu item key
     */
    const handleMenuClick = (e) => {
        setCurrent(e.key);
        const scrollOptions = {
            behavior: "smooth",
            block: "start",
            inline: "nearest",
        };
        if (e.key === "home" && homeRef.current)
            homeRef.current.scrollIntoView(scrollOptions);
        if (e.key === "download" && downloadRef.current)
            downloadRef.current.scrollIntoView(scrollOptions);
        if (e.key === "about" && aboutRef.current)
            aboutRef.current.scrollIntoView(scrollOptions);
        if (e.key === "faq" && faqRef.current)
            faqRef.current.scrollIntoView(scrollOptions);
        if (e.key === "contact" && contactRef.current)
            contactRef.current.scrollIntoView(scrollOptions);
    };

    /**
     * Handle scroll events to update active menu item
     */
    const handleScroll = () => {
        const sections = [
            { key: "home", ref: homeRef },
            { key: "download", ref: downloadRef },
            { key: "about", ref: aboutRef },
            { key: "faq", ref: faqRef },
            { key: "contact", ref: contactRef },
        ];

        const offset = 100;

        sections.forEach((section) => {
            const sectionRef = section.ref.current;
            if (sectionRef) {
                const { top } = sectionRef.getBoundingClientRect();
                if (top <= offset && top + sectionRef.offsetHeight > offset) {
                    setCurrent(section.key);
                }
            }
        });
    };

    // Set up scroll event listener
    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // Load FAQ content
    const faq = t("home.faq.questions", { returnObjects: true });

    return (
        <div className="home-container">
            {/* Navigation Bar */}
            <div className="navbar-container">
                <img
                    src="/android-chrome-512x512.png"
                    alt="Logo"
                    className="navbar-logo"
                />
                <Menu
                    onClick={handleMenuClick}
                    selectedKeys={[current]}
                    mode="horizontal"
                    items={items}
                    className="navbar-menu"
                />
            </div>

            <div className="content">
                {/* Hero Section */}
                <div className="hero-unit" id="home" ref={homeRef}>
                    <img
                        src="/elipse.svg"
                        alt="Ellipse"
                        className="hero-ellipse"
                    ></img>
                    <div className="hero-text">
                        <h1>
                            <img
                                className="hero-logo"
                                src="/logo-hero.svg"
                                alt="Logo_Title"
                            ></img>
                        </h1>
                        <p>{t("home.hero.content")}</p>
                        <Button
                            type="default"
                            ghost
                            size="large"
                            href="#download"
                            className="cta-button"
                        >
                            {t("home.hero.callToAction")}
                        </Button>
                    </div>
                    <div className="hero-image">
                        <img src="/car.svg" alt="Illustration" />
                    </div>
                </div>

                {/* Download Section */}
                <div
                    className="download-section"
                    id="download"
                    ref={downloadRef}
                >
                    <h2>{t("home.download.title")}</h2>
                    <p>{t("home.download.desc")}</p>
                    <div className="download-buttons">
                        <Button
                            type="primary"
                            icon={<AppleOutlined />}
                            size="large"
                            href="https://apps.apple.com/fr/app/glitgo/id6749918848"
                            className="download-button"
                        >
                            {t("home.download.appleDownload")}
                        </Button>
                        <Button
                            type="default"
                            icon={<AndroidOutlined />}
                            size="large"
                            href="https://play.google.com/store/apps/details?id=com.glitgo.glitgo"
                            className="download-button"
                        >
                            {t("home.download.androidDownload")}
                        </Button>
                    </div>
                </div>

                {/* Separator */}
                <div className="separator"></div>

                {/* About Section */}
                <div className="about-section" id="about" ref={aboutRef}>
                    <Col gutter={[16, 16]} align="middle">
                        <Row xs={24} sm={24} md={12}>
                            <Card hoverable className="about-card">
                                <div className="about-card-content">
                                    <img
                                        alt="About Us"
                                        src="/about-illustration.svg"
                                        className="about-image"
                                        style={{
                                            width: "25vw",
                                            height: "auto",
                                        }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <h2>{t("home.about.title1")}</h2>
                                        <p>{t("home.about.desc1")}</p>
                                        <Button
                                            type="primary"
                                            icon={<InfoCircleOutlined />}
                                            size="large"
                                            href="#download"
                                            className="about-button"
                                        >
                                            {t("home.about.btn1")}
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </Row>
                        <Row
                            xs={24}
                            sm={24}
                            md={12}
                            style={{ justifyContent: "center" }}
                        >
                            <div className="about-text">
                                <h2>{t("home.about.title2")}</h2>
                                <p>{t("home.about.desc2")}</p>
                                <Button
                                    type="primary"
                                    icon={<InfoCircleOutlined />}
                                    size="large"
                                    href="#faq"
                                    className="about-button"
                                >
                                    {t("home.about.btn2")}
                                </Button>
                            </div>
                        </Row>
                    </Col>
                </div>

                {/* FAQ Section */}
                <div className="faq-section" id="faq" ref={faqRef}>
                    <h2>{t("home.faq.title")}</h2>
                    <Collapse accordion>
                        {faq.map((item, index) => (
                            <Panel header={item.question} key={index}>
                                <p>{item.answer}</p>
                            </Panel>
                        ))}
                    </Collapse>
                </div>

                {/* Contact Section */}
                <div className="contact-section" id="contact" ref={contactRef}>
                    <h2>{t("home.contact.title")}</h2>
                    <p>{t("home.contact.invitation")}</p>
                    <p>
                        <a href="mailto:support@glitgo.com">
                            support@glitgo.com
                        </a>
                    </p>
                    <p>+33 7 56 80 56 87</p>
                </div>
            </div>

            <Footer />
        </div>
    );
};
